import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { MessageSquare, Mail, Smartphone, Facebook, Instagram, Inbox } from 'lucide-react';
import api from '../services/api';
import { formatDateTime } from '../utils/formatters';

// The lead's latest messages on every chat channel (SMS, email, Messenger,
// Instagram, WhatsApp), so whoever opens the lead sees the last exchange
// without leaving the page. The Notes timeline below stays an internal
// notebook; full threads and replying live in the Inbox.
// Renders nothing for a lead with no messages.

const CHANNELS = {
  sms: { label: 'SMS', Icon: Smartphone },
  email: { label: 'Email', Icon: Mail },
  messenger: { label: 'Messenger', Icon: Facebook },
  instagram: { label: 'Instagram', Icon: Instagram },
  whatsapp: { label: 'WhatsApp', Icon: MessageSquare },
};

const whoLabel = (m) => {
  if (m.direction === 'inbound') return 'Customer';
  if (m.sentByName) return m.sentByName;
  return m.ai ? 'AI' : 'Your team';
};

const LeadRecentMessages = ({ leadId, limit = 5 }) => {
  const { data } = useQuery(
    ['lead-recent-messages', leadId],
    () => api.get(`/conversations/lead/${leadId}/recent`, { params: { limit } }).then((r) => r.data),
    { enabled: !!leadId, staleTime: 30 * 1000 }
  );

  const messages = data?.messages || [];
  if (!messages.length) return null;
  const inboxUrl = `/inbox?leadId=${leadId}`;

  return (
    <div className="card">
      <div className="card-header flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <MessageSquare size={20} className="mr-2" />
          Recent messages
        </h3>
        <Link
          to={inboxUrl}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          <Inbox size={14} /> Reply in Inbox
        </Link>
      </div>
      <ul className="divide-y divide-gray-100">
        {messages.map((m) => {
          const ch = CHANNELS[m.channel] || { label: m.channel, Icon: MessageSquare };
          const inbound = m.direction === 'inbound';
          return (
            <li key={m._id} className={`px-6 py-3 ${inbound ? 'bg-green-50/40' : ''}`}>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1 font-medium text-gray-700">
                  <ch.Icon size={13} /> {ch.label}
                </span>
                <span className={inbound ? 'font-medium text-green-700' : ''}>{whoLabel(m)}</span>
                <span className="ml-auto">{formatDateTime(m.sentAt)}</span>
              </div>
              {m.channel === 'email' && m.subject && (
                <div className="mt-1 text-xs font-medium text-gray-700 truncate">{m.subject}</div>
              )}
              <p className="mt-1 text-sm text-gray-900 break-words line-clamp-2">{m.preview || '(no text)'}</p>
            </li>
          );
        })}
      </ul>
      {data.total > messages.length && (
        <div className="px-6 py-2 border-t border-gray-100 text-right">
          <Link to={inboxUrl} className="text-xs font-medium text-primary-600 hover:text-primary-700">
            View all {data.total} messages in the Inbox
          </Link>
        </div>
      )}
    </div>
  );
};

export default LeadRecentMessages;
